import { useState } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Plus, Trash2 } from 'lucide-react';

export const OwnershipTab = ({ ownerships, llc, user, canEditLLC, totalOwnership, onAddOwnership, onDeleteOwnership }) => {
  const sortedOwnerships = [...ownerships].sort((a, b) => (a.investor_name || '').localeCompare(b.investor_name || ''));
  const isViewer = user?.role === 'viewer';
  const shouldHide = llc?.hide_percentages && isViewer;

  return (
    <TabsContent value="ownership" className="mt-4">
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-serif">Ownership Structure</CardTitle>
            <CardDescription>
              Total allocated: {totalOwnership.toFixed(1)}%
              {totalOwnership < 100 && ` \u2022 ${(100 - totalOwnership).toFixed(1)}% unallocated`}
            </CardDescription>
          </div>
          {canEditLLC && (
            <Button onClick={onAddOwnership} data-testid="add-ownership-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Owner
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {ownerships.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No ownership records yet</p>
          ) : (
            <>
              {/* Mobile View */}
              <div className="md:hidden space-y-2">
                {sortedOwnerships.map((own) => (
                  <div key={own.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <span className="font-medium text-slate-800">{own.investor_name}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-amber-700">
                        {!shouldHide ? `${own.percentage.toFixed(2)}%` : <span className="text-slate-400 italic">Hidden</span>}
                      </span>
                      {canEditLLC && (
                        <Button variant="ghost" size="icon" className="text-rose-600 hover:text-rose-700 h-8 w-8" onClick={() => onDeleteOwnership(own.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Partner</TableHead>
                      <TableHead className="text-right">Ownership %</TableHead>
                      {canEditLLC && <TableHead className="w-[50px]"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedOwnerships.map((own) => (
                      <TableRow key={own.id}>
                        <TableCell className="font-medium">{own.investor_name}</TableCell>
                        <TableCell className="text-right font-mono">
                          {!shouldHide ? `${own.percentage.toFixed(5)}%` : <span className="text-slate-400 italic">Hidden</span>}
                        </TableCell>
                        {canEditLLC && (
                          <TableCell>
                            <Button variant="ghost" size="icon" className="text-rose-600 hover:text-rose-700" onClick={() => onDeleteOwnership(own.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
};
